import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DailyContentFormData } from '../types';
import {
  SOCIAL_PLATFORMS,
  CONTENT_PERSONALITIES,
  AUDIENCE_FOCUS_OPTIONS,
  DAILY_GOALS,
  CONTENT_THEMES,
  DAILY_QUANTITY_OPTIONS,
  IDENTITY_TYPE_OPTIONS,
  CREATOR_PERSONALITY_OPTIONS,
  CREATOR_TONE_OPTIONS,
  CONTENT_STYLE_OPTIONS,
  SOCIAL_POST_COST,
} from '../types';
import type { BusinessProfile } from '@/hooks/useBusinessProfiles';

interface DailyContentFormProps {
  profiles: BusinessProfile[];
  profilesLoading: boolean;
  formData: DailyContentFormData;
  onChange: (data: DailyContentFormData) => void;
  onGenerate: () => void;
  onStopGeneration: () => void;
  isGenerating: boolean;
  creditsRemaining: number;
  hasEnoughCredits: (cost: number) => boolean;
  selectedProfileId?: string | null;
  onProfileSelect?: (profileId: string | null) => void;
}

const inputClass =
  'w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200';

function CollapsibleSection({
  icon,
  title,
  subtitle,
  defaultOpen = false,
  children,
}: {
  icon: string;
  title: string;
  subtitle: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-stone-200 overflow-hidden">
      <button
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center gap-3 px-4 py-3.5 bg-stone-50 hover:bg-stone-100 transition-colors cursor-pointer text-left"
      >
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-stone-200 shrink-0">
          <i className={`${icon} text-stone-500 text-sm`} />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-bold text-stone-800">{title}</span>
          <p className="text-xs text-stone-400 truncate">{subtitle}</p>
        </div>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg shrink-0`} />
      </button>
      {open && (
        <div className="px-4 py-4 bg-white border-t border-stone-100 space-y-4">{children}</div>
      )}
    </div>
  );
}

export default function DailyContentForm({
  profiles,
  profilesLoading,
  formData,
  onChange,
  onGenerate,
  onStopGeneration,
  isGenerating,
  creditsRemaining,
  hasEnoughCredits,
  selectedProfileId,
  onProfileSelect,
}: DailyContentFormProps) {
  const set = <K extends keyof DailyContentFormData>(field: K, value: DailyContentFormData[K]) => {
    if (field === 'businessName') onProfileSelect?.(null);
    onChange({ ...formData, [field]: value });
  };

  const toggleTheme = (themeId: string) => {
    const current = formData.contentThemes;
    const updated = current.includes(themeId)
      ? current.filter((t) => t !== themeId)
      : [...current, themeId];
    onChange({ ...formData, contentThemes: updated });
  };

  const quantityCost = formData.quantity === '7' ? SOCIAL_POST_COST * 3 : formData.quantity === '3' ? SOCIAL_POST_COST * 2 : SOCIAL_POST_COST;

  const canGenerate =
    formData.businessName.trim() &&
    formData.platform &&
    formData.contentPersonality &&
    hasEnoughCredits(quantityCost);

  return (
    <div className="space-y-4">
      {/* Helper banner */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-emerald-500 shrink-0">
            <i className="ri-calendar-2-line text-white text-sm" />
          </div>
          <div>
            <p className="text-xs font-bold text-emerald-800">Daily Content Mode</p>
            <p className="text-xs text-emerald-600 mt-0.5 leading-relaxed">
              Perfect for staying active and visible online — even when you&apos;re not running a promotion.
            </p>
          </div>
        </div>
      </div>

      {/* Profile quick-fill */}
      {!profilesLoading && profiles.length > 0 && (
        <div className="bg-white rounded-2xl border border-stone-100 p-4">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-building-2-line text-orange-400 text-sm" />
            <span className="text-xs font-semibold text-stone-700 uppercase tracking-wider">
              Quick-fill from profile
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {profiles.map((p) => (
              <button
                key={p.id}
                onClick={() => {
                  onProfileSelect?.(p.id);
                  onChange({ ...formData, businessName: p.business_name });
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all duration-200 cursor-pointer ${
                  selectedProfileId === p.id
                    ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-300'
                    : 'border-stone-200 bg-stone-50 hover:border-orange-300 hover:bg-orange-50'
                }`}
              >
                {p.logo_url ? (
                  <img
                    src={p.logo_url}
                    alt={p.business_name}
                    className="w-6 h-6 rounded-md object-contain shrink-0 bg-white border border-stone-100"
                  />
                ) : (
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-orange-100 to-amber-50 shrink-0">
                    <i className="ri-building-2-line text-orange-400 text-xs" />
                  </div>
                )}
                <span className="text-xs font-semibold text-stone-700">{p.business_name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main form card */}
      <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-100">
          <h2 className="text-sm font-bold text-stone-900">Content Details</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            Generate daily social content even when you&apos;re not running a promotion
          </p>
        </div>

        <div className="px-5 py-5 space-y-5">
          <CollapsibleSection
            icon="ri-user-settings-line"
            title="Identity"
            subtitle="Account type, profile, platform, and audience"
            defaultOpen
          >
          {/* Identity Type */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Identity Type <span className="text-orange-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {IDENTITY_TYPE_OPTIONS.map((identity) => (
                <button
                  key={identity.id}
                  onClick={() => set('identityType', identity.id)}
                  className={`flex flex-col items-start gap-1 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    formData.identityType === identity.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <i className={`${identity.icon} text-sm`} />
                  <span className="text-xs font-bold">{identity.label}</span>
                  <span className={`text-xs leading-tight ${formData.identityType === identity.id ? 'text-orange-100' : 'text-stone-400'}`}>
                    {identity.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Business Name */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              {formData.identityType === 'creator' ? 'Creator / Brand Name' : formData.identityType === 'hybrid' ? 'Creator or Business Name' : 'Business Name'} <span className="text-orange-500">*</span>
            </label>
            <input
              type="text"
              value={formData.businessName}
              onChange={(e) => set('businessName', e.target.value)}
              placeholder={formData.identityType === 'creator' ? 'e.g. Coach Maya Fit' : 'e.g. Rivera Plumbing Co.'}
              className={inputClass}
            />
          </div>

          {formData.identityType !== 'business' && (
            <div className="rounded-xl border border-stone-200 bg-stone-50 px-4 py-4 space-y-4">
              <div className="flex items-center gap-2">
                <i className="ri-user-star-line text-orange-400 text-sm" />
                <span className="text-xs font-bold text-stone-800 uppercase tracking-wider">Creator Profile</span>
              </div>
              <input
                type="text"
                value={formData.creatorNiche}
                onChange={(e) => set('creatorNiche', e.target.value)}
                placeholder="Niche: fitness, gaming, lifestyle, business..."
                className={`${inputClass} bg-white`}
              />
              <textarea
                value={formData.contentPillars}
                onChange={(e) => set('contentPillars', e.target.value)}
                placeholder="Content pillars: topics you post about regularly"
                rows={2}
                maxLength={300}
                className={`${inputClass} resize-none bg-white`}
              />
              <input
                type="text"
                value={formData.creatorAudienceType}
                onChange={(e) => set('creatorAudienceType', e.target.value)}
                placeholder="Audience type: fans, beginners, locals, entrepreneurs..."
                className={`${inputClass} bg-white`}
              />
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                  Personality
                </label>
                <div className="flex flex-wrap gap-2">
                  {CREATOR_PERSONALITY_OPTIONS.map((personality) => (
                    <button
                      key={personality}
                      onClick={() => set('creatorPersonality', formData.creatorPersonality === personality ? '' : personality)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        formData.creatorPersonality === personality
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : 'bg-white border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                      }`}
                    >
                      {personality}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
                  Tone
                </label>
                <div className="flex flex-wrap gap-2">
                  {CREATOR_TONE_OPTIONS.map((tone) => (
                    <button
                      key={tone}
                      onClick={() => set('creatorTone', formData.creatorTone === tone ? '' : tone)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        formData.creatorTone === tone
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : 'bg-white border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                      }`}
                    >
                      {tone}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Platform */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Platform <span className="text-orange-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SOCIAL_PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => set('platform', p.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    formData.platform === p.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  <i className={`${p.icon} text-sm shrink-0`} />
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content Personality */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Content Personality <span className="text-orange-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {CONTENT_PERSONALITIES.map((p) => (
                <button
                  key={p.id}
                  onClick={() => set('contentPersonality', formData.contentPersonality === p.id ? '' : p.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    formData.contentPersonality === p.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <i className={`${p.icon} text-sm shrink-0`} />
                  <div>
                    <div className="text-xs font-bold">{p.label}</div>
                    <div className={`text-xs font-normal mt-0.5 ${formData.contentPersonality === p.id ? 'text-orange-100' : 'text-stone-400'}`}>
                      {p.desc}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Audience Focus */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Audience Focus
            </label>
            <div className="grid grid-cols-2 gap-2">
              {AUDIENCE_FOCUS_OPTIONS.map((a) => (
                <button
                  key={a.id}
                  onClick={() => set('audienceFocus', formData.audienceFocus === a.id ? '' : a.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    formData.audienceFocus === a.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  <i className={`${a.icon} text-sm shrink-0`} />
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Location
              <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="e.g. Hartland, MI"
              className={inputClass}
            />
          </div>
          </CollapsibleSection>

          <CollapsibleSection
            icon="ri-calendar-check-line"
            title="Content Plan"
            subtitle="Goal, themes, style, variety, and quantity"
          >
          {/* Daily Content Goal */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Daily Content Goal
            </label>
            <div className="grid grid-cols-2 gap-2">
              {DAILY_GOALS.map((g) => (
                <button
                  key={g.id}
                  onClick={() => set('dailyGoal', formData.dailyGoal === g.id ? '' : g.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    formData.dailyGoal === g.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <i className={`${g.icon} text-sm shrink-0`} />
                  <div>
                    <div className="text-xs font-bold">{g.label}</div>
                    <div className={`text-xs font-normal mt-0.5 ${formData.dailyGoal === g.id ? 'text-orange-100' : 'text-stone-400'}`}>
                      {g.desc}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Content Themes */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Content Themes
              <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(multi-select)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {CONTENT_THEMES.map((t) => {
                const isActive = formData.contentThemes.includes(t.id);
                return (
                  <button
                    key={t.id}
                    onClick={() => toggleTheme(t.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                    }`}
                  >
                    {isActive && <i className="ri-check-line mr-1 text-xs" />}
                    {t.label}
                  </button>
                );
              })}
            </div>
            {formData.contentThemes.length === 0 && (
              <p className="text-xs text-stone-400 mt-1.5">AI will choose the best themes for your business</p>
            )}
          </div>

          {/* Content Style */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Creative Style Engine
              <span className="normal-case font-normal text-stone-400 ml-1 tracking-normal">(optional)</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {CONTENT_STYLE_OPTIONS.map((style) => (
                <button
                  key={style.id}
                  onClick={() => set('contentStyle', formData.contentStyle === style.id ? '' : style.id)}
                  className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-xl border text-left transition-all duration-200 cursor-pointer ${
                    formData.contentStyle === style.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <span className="text-xs font-bold">{style.label}</span>
                  <span className={`text-xs leading-tight ${formData.contentStyle === style.id ? 'text-orange-100' : 'text-stone-400'}`}>
                    {style.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Avoid Repetition */}
          <div>
            <button
              onClick={() => set('avoidRepetition', !formData.avoidRepetition)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all duration-200 cursor-pointer text-left ${
                formData.avoidRepetition
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-stone-200 bg-stone-50 hover:border-orange-200'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 transition-all duration-200 ${formData.avoidRepetition ? 'bg-orange-500' : 'bg-stone-200'}`}>
                <i className={`ri-shuffle-line text-sm ${formData.avoidRepetition ? 'text-white' : 'text-stone-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-bold ${formData.avoidRepetition ? 'text-orange-700' : 'text-stone-700'}`}>
                  Avoid Repetition
                </div>
                <div className={`text-xs mt-0.5 ${formData.avoidRepetition ? 'text-orange-500' : 'text-stone-400'}`}>
                  Vary hooks, angles, phrasing &amp; CTA types across posts
                </div>
              </div>
              <div className={`relative w-10 h-5 rounded-full transition-all duration-300 shrink-0 ${formData.avoidRepetition ? 'bg-orange-500' : 'bg-stone-300'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all duration-300 ${formData.avoidRepetition ? 'left-5' : 'left-0.5'}`} />
              </div>
            </button>
          </div>

          {/* Generate Quantity */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
              Generate Quantity
            </label>
            <div className="grid grid-cols-3 gap-2">
              {DAILY_QUANTITY_OPTIONS.map((q) => (
                <button
                  key={q.id}
                  onClick={() => set('quantity', q.id)}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    formData.quantity === q.id
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300'
                  }`}
                >
                  <i className={`${q.icon} text-base`} />
                  <span className="text-xs font-bold">{q.label}</span>
                  <span className={`text-xs font-normal text-center leading-tight ${formData.quantity === q.id ? 'text-orange-100' : 'text-stone-400'}`}>
                    {q.desc}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* 7-Day CTA highlight */}
          {formData.quantity === '7' && (
            <div className="rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 px-4 py-3 flex items-center gap-3">
              <i className="ri-calendar-2-line text-emerald-500 text-lg shrink-0" />
              <div>
                <p className="text-xs font-bold text-emerald-800">7-Day Content Plan</p>
                <p className="text-xs text-emerald-600 mt-0.5">
                  AI will generate 7 unique posts with varied types, angles, and CTAs — one for each day of the week.
                </p>
              </div>
            </div>
          )}
          </CollapsibleSection>

          {/* Generate button */}
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between px-1">
              <span className="text-xs text-stone-400">Your balance</span>
              <span className={`text-xs font-bold ${hasEnoughCredits(quantityCost) ? 'text-stone-700' : 'text-red-500'}`}>
                {creditsRemaining} credits
              </span>
            </div>

            {/* 7-Day quick action button */}
            {formData.quantity !== '7' && (
              <button
                onClick={() => {
                  onChange({ ...formData, quantity: '7' });
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-emerald-300 text-emerald-600 text-xs font-semibold hover:bg-emerald-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-2-line text-sm" />
                Generate 7 Days of Content
              </button>
            )}

            <button
              onClick={onGenerate}
              disabled={!canGenerate || isGenerating}
              className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-sm transition-all duration-200 whitespace-nowrap ${
                canGenerate && !isGenerating
                  ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                  : 'bg-stone-100 text-stone-400 cursor-not-allowed'
              }`}
            >
              {isGenerating ? (
                <>
                  <i className="ri-loader-4-line text-base animate-spin" />
                  {formData.quantity === '7' ? 'Building your 7-day plan...' : 'Generating content...'}
                </>
              ) : (
                <>
                  <i className="ri-sparkling-2-fill text-base" />
                  {formData.quantity === '7'
                    ? `Generate 7-Day Plan — ${quantityCost} Credits`
                    : formData.quantity === '3'
                    ? `Generate 3 Posts — ${quantityCost} Credits`
                    : `Generate Post — ${quantityCost} Credits`}
                </>
              )}
            </button>
            {isGenerating && (
              <button
                type="button"
                onClick={onStopGeneration}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-stone-300 bg-white text-stone-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700 font-bold text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-stop-circle-line text-base" />
                Stop and edit form
              </button>
            )}

            {!hasEnoughCredits(quantityCost) && !isGenerating && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-xl">
                <i className="ri-error-warning-line text-sm shrink-0" />
                <span className="flex-1">Not enough credits. Please upgrade or purchase more.</span>
                <Link to="/pricing" className="shrink-0 text-xs font-bold text-red-600 underline underline-offset-2 whitespace-nowrap cursor-pointer">
                  Upgrade
                </Link>
              </div>
            )}

            <div className="flex items-center justify-center gap-1.5 text-xs text-stone-400">
              <i className="ri-sparkling-2-line text-stone-300" />
              <span>
                {quantityCost} credits · captions + image directions included
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
